import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';

export interface AIProcessingDialogData {
  message: string;
}

@Component({
  selector: 'app-ai-processing-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatIconModule
  ],
  template: `
    <div class="ai-processing-container">
      <div class="ai-header">
        <mat-icon class="ai-icon">psychology</mat-icon>
        <h2>AI Contract Analysis</h2>
      </div>

      <div class="spinner-container">
        <mat-spinner diameter="60"></mat-spinner>
      </div>

      <div class="status-message">
        {{ data.message }}
      </div>

      <p class="info-text">
        AI processing can take a minute or more depending on the size and complexity of the contract document.
        Please wait while we analyze the content.
      </p>

      <div class="processing-steps">
        <div class="step" [class.active]="currentStep === 0" [class.completed]="currentStep > 0">
          <mat-icon>cloud_upload</mat-icon>
          <span>Uploading</span>
        </div>
        <div class="step" [class.active]="currentStep === 1" [class.completed]="currentStep > 1">
          <mat-icon>description</mat-icon>
          <span>Extracting</span>
        </div>
        <div class="step" [class.active]="currentStep === 2" [class.completed]="currentStep > 2">
          <mat-icon>search</mat-icon>
          <span>Analyzing</span>
        </div>
        <div class="step" [class.active]="currentStep === 3" [class.completed]="currentStep > 3">
          <mat-icon>people</mat-icon>
          <span>Matching</span>
        </div>
        <div class="step" [class.active]="currentStep >= 4" [class.completed]="false">
          <mat-icon>check_circle</mat-icon>
          <span>Finalizing</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .ai-processing-container {
      padding: 32px;
      text-align: center;
      min-width: 520px;
      max-width: 600px;
    }

    .ai-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-bottom: 24px;
    }

    .ai-header h2 {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 500;
      color: #333;
    }

    .ai-icon {
      font-size: 32px;
      width: 32px;
      height: 32px;
      color: #1976d2;
    }

    .spinner-container {
      display: flex;
      justify-content: center;
      margin: 24px 0;
    }

    .status-message {
      font-size: 1.1rem;
      font-weight: 500;
      color: #1976d2;
      margin-bottom: 16px;
      min-height: 28px;
    }

    .info-text {
      color: #666;
      font-size: 0.9rem;
      line-height: 1.5;
      margin: 0 0 24px 0;
      padding: 0 16px;
    }

    .processing-steps {
      display: flex;
      justify-content: space-between;
      padding: 16px 12px;
      background: #f5f5f5;
      border-radius: 8px;
      margin-top: 8px;
    }

    .step {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      color: #888;
      font-size: 0.7rem;
      transition: color 0.3s ease;
      flex: 1;
    }

    .step mat-icon {
      font-size: 24px;
      width: 24px;
      height: 24px;
      color: #999;
      transition: color 0.3s ease;
    }

    .step.active {
      color: #1976d2;
      font-weight: 500;
    }

    .step.active mat-icon {
      color: #1976d2;
    }

    .step.completed {
      color: #4caf50;
    }

    .step.completed mat-icon {
      color: #4caf50;
    }
  `]
})
export class AIProcessingDialogComponent implements OnInit, OnDestroy {
  currentStep = -1; // Start at -1 so nothing is highlighted initially
  private stepTimer: any;

  constructor(
    public dialogRef: MatDialogRef<AIProcessingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AIProcessingDialogData,
    private cdr: ChangeDetectorRef
  ) {
    // Disable closing the dialog by clicking outside or pressing escape
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    // Progress through steps over time to give user visual feedback
    // Step 0 (Uploading) at ~3s, Step 1 (Extracting) at ~6s, Step 2 (Analyzing) at ~9s
    // Step 3 (Matching) at ~40s, Step 4 (Finalizing) at ~70s
    this.stepTimer = setInterval(() => {
      if (this.currentStep < 2) {
        this.currentStep++;
        this.cdr.detectChanges();
      } else if (this.currentStep === 2) {
        // Stay on step 2 (Analyzing) for a while, then move to Matching
        clearInterval(this.stepTimer);
        this.stepTimer = setTimeout(() => {
          this.currentStep = 3;
          this.cdr.detectChanges();
          // Schedule step 4 (Finalizing) after another 30 seconds
          this.stepTimer = setTimeout(() => {
            this.currentStep = 4;
            this.cdr.detectChanges();
          }, 30000);
        }, 30000); // 40 seconds total (~10 + 30)
      }
    }, 3000); // 3 seconds per step for first 3 steps
  }

  ngOnDestroy(): void {
    if (this.stepTimer) {
      clearInterval(this.stepTimer);
      clearTimeout(this.stepTimer);
    }
  }

  /**
   * Update the status message displayed in the dialog
   */
  updateMessage(message: string): void {
    this.data.message = message;
  }
}
